import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { mapValues, isEmpty } from 'lodash-es';

import AdvancedChartLegend from '../AdvancedChartLegend';
import Skeleton from '../../../../../components/Skeleton';
import { SmartChart } from '../../../../../components/Chart';
import useAdvancedLegend, {
  useToggleSeriesVisibility,
  useHoveredItemClassName,
} from '../../hooks/useAdvancedLegend';
import { marketShareConfig, colors } from './config';
import { buildAdvancedLegendItems } from '../../utils/build-advanced-legend-items';
import { createEdgeLinesRenderer } from '../../utils/chart-edge-lines';

const emptyObject = {};

const SharesChart = ({
  metricSeries,
  chartType,
  apps,
  isPending,
  isCumulative,
  isPress,
  partialFilters,
  chartCaption,
  withEdgeLines,
}) => {
  const chartId = useId();
  const chartRef = useRef(null);
  const maskTimeoutRef = useRef(null);
  const { current: renderEdgeLines } = useRef(createEdgeLinesRenderer());

  const [isChartRendered, setChartRendered] = useState(false);

  const appsRefNos = useMemo(() => apps.map((app) => app.unified_ref_no), [apps]);
  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut } = useAdvancedLegend();
  const hoveredItemClassName = useHoveredItemClassName(metricSeries, hoveredItem, appsRefNos);
  useToggleSeriesVisibility(chartRef, activeItem);

  const adjustedChartType = isCumulative ? 'spline' : chartType;

  const sortedMetric = useMemo(() => {
    if (isEmpty(metricSeries)) return emptyObject;

    return apps.reduce(
      (acc, app) => ({
        ...acc,
        [app.unified_ref_no]: metricSeries[app.unified_ref_no] || [],
      }),
      {}
    );
  }, [apps, metricSeries]);

  const metaSeriesData = useMemo(() => {
    const appByRefNo = apps.reduce(
      (acc, app, index) => ({
        ...acc,
        [app.unified_ref_no]: { ...app, color: colors[index] },
      }),
      {}
    );

    return mapValues(sortedMetric, (_, appRefNo) => ({
      type: adjustedChartType,
      color: appByRefNo[appRefNo]?.color,
      name: `${appByRefNo[appRefNo]?.nickname || 'Unknown'}`,
      id: appRefNo,
      // manage visible via useToggleSeriesVisibility to preserve built-in animation
    }));
  }, [sortedMetric, apps, adjustedChartType]);

  const edgeTicks = useMemo(() => {
    const withData = Object.values(sortedMetric).find((points) => points.length > 1);
    return withData ? [withData[0][0], withData[withData.length - 1][0]] : null;
  }, [sortedMetric]);

  const chartConfig = useMemo(() => {
    // Workaround: prevent chart's events until the chart animations are finished.
    const toggleChartMask = () => {
      setChartRendered(true);
      clearTimeout(maskTimeoutRef.current);
      maskTimeoutRef.current = setTimeout(() => setChartRendered(false), 1000); // Duration should be >= chart animation duration
    };

    return marketShareConfig({
      chartType: adjustedChartType,
      unit: isCumulative ? 'pp' : '%',
      activeItem,
      onClick,
      onMouseOver,
      onMouseOut,
      toggleChartMask,
      isPress,
      renderEdgeLines: withEdgeLines ? renderEdgeLines : undefined,
      edgeTicks,
    });
  }, [
    adjustedChartType,
    isCumulative,
    activeItem,
    onClick,
    onMouseOver,
    onMouseOut,
    isPress,
    withEdgeLines,
    renderEdgeLines,
    edgeTicks,
  ]);

  const legendOptions = useMemo(
    () => buildAdvancedLegendItems(Object.values(metaSeriesData)),
    [metaSeriesData]
  );

  // Cleanup timeout on unmount to avoid memory leaks
  useEffect(() => () => clearTimeout(maskTimeoutRef.current), []);

  return (
    <div className={cx('shares-chart', hoveredItemClassName)}>
      {chartCaption && <p className="chart-caption">{chartCaption}</p>}
      <div className={cx('chart-container', { 'pointer-events-none': isChartRendered })}>
        {isPending ? (
          <Skeleton height={340} className="d-flex" />
        ) : (
          <>
            <div onMouseOut={onMouseOut} onBlur={onMouseOut}>
              <SmartChart
                ref={chartRef}
                chartId={`shares-chart-${chartId}`}
                series={sortedMetric}
                metaSeriesData={metaSeriesData}
                config={chartConfig}
                allowPartialPeriods
                filters={partialFilters}
              />
            </div>
            <AdvancedChartLegend
              items={legendOptions}
              activeItem={activeItem?.id || hoveredItem?.id}
              onClick={onClick}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            />
          </>
        )}
      </div>
    </div>
  );
};

SharesChart.propTypes = {
  metricSeries: T.object.isRequired,
  chartType: T.string.isRequired,
  apps: T.array.isRequired,
  isPending: T.bool.isRequired,
  isCumulative: T.bool.isRequired,
  isPress: T.bool,
  partialFilters: T.object.isRequired,
  chartCaption: T.node,
  withEdgeLines: T.bool,
};

export default SharesChart;
